import React, {useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {useNavigate} from 'react-router-dom';
import {routes} from '../../../utils/constants/routes';
import LinkIcon from '@mui/icons-material/Link';
import strings from '../../../common/Translation/Translate';
import './styles.scss';

dayjs.extend(advancedFormat);

const MAX_VISIBLE = 5;

const isCurrentItem = (item: any, currentLineItemId?: number) =>
    item.current === true || Number(item.id) === Number(currentLineItemId);

const getVisibleBookings = (
    bookings: any[],
    expanded: boolean,
    currentLineItemId?: number,
) => {
    if (expanded || bookings.length <= MAX_VISIBLE) {
        return bookings;
    }

    const visible = bookings.slice(0, MAX_VISIBLE);
    const currentIndex = bookings.findIndex((item) =>
        isCurrentItem(item, currentLineItemId),
    );

    if (currentIndex >= MAX_VISIBLE) {
        return [...visible.slice(0, MAX_VISIBLE - 1), bookings[currentIndex]];
    }

    return visible;
};

const getChipStyles = (status: string) => {
    switch (status?.toUpperCase()) {
        case 'CONFIRMED':
            return {backgroundColor: '#E6F4EA', color: '#188038'};
        case 'TENTATIVE':
            return {backgroundColor: '#FFF4E5', color: '#D17A00'};
        case 'WAITLISTED':
            return {backgroundColor: '#E8F0FE', color: '#1967D2'};
        case 'CANCELLED':
            return {backgroundColor: '#FDECEA', color: '#D93025'};
        case 'INQUIRY':
            return {backgroundColor: '#E2E3E5', color: '#495057'};
        case 'ACTUALIZED':
            return {backgroundColor: '#800080', color: '#fff'};
        default:
            return {backgroundColor: '#F5F5F5', color: '#616161'};
    }
};

const ACTUALIZATION_FINALIZED_CHIP_STYLES = {backgroundColor: '#800080', color: '#fff'};

const formatEventSubtype = (value: unknown): string => {
    if (value == null || value === '') return '';
    if (typeof value === 'object' && value !== null) {
        const obj = value as { label?: string; value?: string };
        return (obj.label || obj.value || '').trim();
    }
    return String(value).trim();
};

const formatTimeRange = (startDate: unknown, endDate: unknown): string => {
    const start = startDate ? dayjs(startDate as string) : null;
    const end = endDate ? dayjs(endDate as string) : null;
    if (start?.isValid() && end?.isValid()) {
        return `${start.format('hh:mm A')} - ${end.format('hh:mm A')}`;
    }
    if (start?.isValid()) return start.format('hh:mm A');
    if (end?.isValid()) return end.format('hh:mm A');
    return '';
};

const getRelatedBookingStatusDisplay = (
    item: any,
): {label: string; chipSx: Record<string, string | number>} => {
    if (item.actualizationFinalized) {
        return {
            label: strings.bookingDetail_status_actualization_finalized,
            chipSx: ACTUALIZATION_FINALIZED_CHIP_STYLES,
        };
    }

    const rawStatus = String(item.status || '').toUpperCase();
    if (item.hasActualization && rawStatus === 'CONFIRMED') {
        return {
            label: strings.actualization_text,
            chipSx: getChipStyles('ACTUALIZED'),
        };
    }

    return {
        label: rawStatus || '—',
        chipSx: getChipStyles(rawStatus),
    };
};

interface RelatedBookingProps {
    relatedBookingLineItems: any;
    currentLineItemId?: number;
}

const RelatedBooking = ({
    relatedBookingLineItems: relatedBookings,
    currentLineItemId,
}: RelatedBookingProps) => {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);

    const handleRowClick = (lineItemId: number) => {
        navigate(`${routes.bookings}/${lineItemId}`);
    };

    const visibleBookings = useMemo(
        () => {
            if (!relatedBookings || relatedBookings.length <= 1) {
                return [];
            }
            return getVisibleBookings(relatedBookings, expanded, currentLineItemId);
        },
        [relatedBookings, expanded, currentLineItemId],
    );

    if (!relatedBookings || relatedBookings.length <= 1) {
        return null;
    }

    const hiddenCount = relatedBookings.length - MAX_VISIBLE;

    const headerCellSx = {
        fontWeight: 700,
        color: '#666',
        textTransform: 'uppercase',
        fontSize: 11,
        whiteSpace: 'nowrap',
        backgroundColor: '#fafafa',
        py: 0.75,
        px: 2,
    } as const;

    const bodyCellSx = {
        py: 0.75,
        px: 2,
    } as const;

    const revenueCellSx = {
        display: { xs: 'none', sm: 'table-cell' },
    } as const;

    return (
        <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden', mt: 3 }}>
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    py: 1,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                })}
            >
                <LinkIcon />
                <Typography variant="subtitle1" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {strings.booking_details_related_bookings}
                </Typography>
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx}>{strings.venueDetails_text_date}</TableCell>
                            <TableCell sx={headerCellSx}>{strings.venueTitle}</TableCell>
                            <TableCell sx={headerCellSx}>{strings.paymentDetails_column_status}</TableCell>
                            <TableCell align="right" sx={{ ...headerCellSx, ...revenueCellSx }}>
                                {strings.booking_details_related_revenue}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleBookings.map((item: any) => {
                            const isCurrent = isCurrentItem(item, currentLineItemId);
                            const statusDisplay = getRelatedBookingStatusDisplay(item);
                            const eventSubtype = formatEventSubtype(item.eventSubtype);
                            const timeRange = formatTimeRange(item.startDate, item.endDate);
                            const partOfDayLabel = String(item.partOfDayName || '').trim();
                            const timeSlotLine = [timeRange, partOfDayLabel].filter(Boolean).join(' · ');

                            return (
                                <TableRow
                                    key={item.id}
                                    onClick={isCurrent ? undefined : () => handleRowClick(item.id)}
                                    sx={{
                                        '&:last-child td': {border: 0},
                                        ...(isCurrent
                                            ? {
                                                bgcolor: 'rgba(25, 103, 210, 0.06)',
                                                boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
                                                cursor: 'default',
                                            }
                                            : {
                                                cursor: 'pointer',
                                                '&:hover': {backgroundColor: 'rgba(0, 0, 0, 0.04)'},
                                            }),
                                    }}
                                >
                                    <TableCell sx={bodyCellSx}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            color={isCurrent ? 'primary.main' : 'text.primary'}
                                            sx={{lineHeight: 1.25}}
                                        >
                                            {dayjs(item.startDate).format('MMM DD, YYYY')}
                                        </Typography>
                                        {timeSlotLine ? (
                                            <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.2}}>
                                                {timeSlotLine}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap'}}>
                                            <Stack spacing={0.15} sx={{minWidth: 0, flex: 1}}>
                                                <Typography
                                                    variant="body2"
                                                    color={isCurrent ? 'primary.main' : 'text.primary'}
                                                    fontWeight={isCurrent ? 600 : 700}
                                                    sx={{lineHeight: 1.4}}
                                                >
                                                    {item.venueName}
                                                </Typography>
                                                {eventSubtype ? (
                                                    <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.4}}>
                                                        {eventSubtype}
                                                    </Typography>
                                                ) : null}
                                            </Stack>
                                            {isCurrent && (
                                                <Chip
                                                    label={strings.paymentDetails_currentItem_label}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{height: 20, fontSize: 10, fontWeight: 700, flexShrink: 0}}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={bodyCellSx}>
                                        <Chip
                                            label={statusDisplay.label}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontWeight: 600,
                                                fontSize: 10,
                                                ...statusDisplay.chipSx,
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={{ ...bodyCellSx, ...revenueCellSx }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                            sx={{fontVariantNumeric: 'tabular-nums'}}>
                                            {item.formattedRevenue || '—'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {!expanded && hiddenCount > 0 ? (
                <Button
                    size="small"
                    onClick={() => setExpanded(true)}
                    sx={{
                        display: 'block',
                        width: '100%',
                        borderRadius: 0,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 12,
                        color: 'primary.main',
                        borderTop: '1px solid #e0e0e0',
                        bgcolor: '#fafafa',
                    }}
                >
                    {hiddenCount === 1 ? 'View 1 more' : `+${hiddenCount} more`}
                </Button>
            ) : null}
        </Paper>
    );
};

export default RelatedBooking;
